import React from 'react';
import { RoomBillingCalculation } from '../../../../types/portvilleBilling';

interface RoomBillingTableProps {
  calculations: RoomBillingCalculation[];
  loading?: boolean;
}

const RoomBillingTable: React.FC<RoomBillingTableProps> = ({
  calculations,
  loading = false
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatUsage = (usage: number | null | undefined, unit: string) => {
    if (usage === null || usage === undefined) {
      return '-';
    }
    return `${usage.toLocaleString()} ${unit}`;
  };

  const formatRatio = (ratio: number) => {
    return `${(ratio * 100).toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (calculations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-lg mb-2">💰</div>
          <p className="text-gray-500 text-lg">호실별 요금 계산 결과가 없습니다</p>
          <p className="text-gray-400 text-sm mt-1">요금 계산 버튼을 클릭하여 계산해보세요</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* 테이블 헤더 */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            호실별 요금 계산 결과 ({calculations.length}개 호실)
          </h3>
        </div>
      </div>

      {/* 테이블 */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                호실
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                입주자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                사용량 (개별/공동)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                전기 요금
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                수도 요금
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                기타 요금
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                총 요금
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                계산 비율
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {calculations.map((calc) => (
              <tr key={calc.roomId} className="hover:bg-gray-50">
                {/* 호실 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {calc.roomNumber}
                  </div>
                </td>

                {/* 입주자 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {calc.residentName}
                  </div>
                </td>

                {/* 사용량 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div className="space-y-1">
                      <div>
                        <span className="font-medium">전기:</span>
                        <div className="text-xs text-gray-600 ml-2">
                          <div>개별: {formatUsage(calc.usage.electricityIndividual, 'kWh')}</div>
                          <div>공동: {formatUsage(calc.usage.electricityCommon, 'kWh')}</div>
                        </div>
                      </div>
                      <div>
                        <span className="font-medium">수도:</span>
                        <div className="text-xs text-gray-600 ml-2">
                          <div>개별: {formatUsage(calc.usage.waterIndividual, '톤')}</div>
                          <div>공동: {formatUsage(calc.usage.waterCommon, '톤')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>

                {/* 전기 요금 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div className="font-medium">{formatCurrency(calc.electricity.totalFee)}</div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>기본: {formatCurrency(calc.electricity.baseFee)}</div>
                      <div>공동: {formatCurrency(calc.electricity.commonFee)}</div>
                      <div>개별: {formatCurrency(calc.electricity.individualFee)}</div>
                    </div>
                  </div>
                </td>

                {/* 수도 요금 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div className="font-medium">{formatCurrency(calc.water.totalFee)}</div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>개별: {formatCurrency(calc.water.individualFee)}</div>
                      <div>공동: {formatCurrency(calc.water.commonFee)}</div>
                    </div>
                  </div>
                </td>

                {/* 기타 요금 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div className="font-medium">{formatCurrency(calc.electricity.fund + calc.electricity.offsetFee)}</div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>전력기금: {formatCurrency(calc.electricity.fund)}</div>
                      <div>상계처리: {formatCurrency(calc.electricity.offsetFee)}</div>
                    </div>
                  </div>
                </td>

                {/* 총 요금 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">
                    {formatCurrency(
                      calc.electricity.totalFee + 
                      calc.water.totalFee + 
                      calc.electricity.fund + 
                      calc.electricity.offsetFee
                    )}
                  </div>
                </td>

                {/* 계산 비율 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-xs text-gray-600 space-y-1">
                    <div><span className="font-medium">전기면적비율:</span> {formatRatio(calc.ratios.electricityAreaContribution)}</div>
                    <div><span className="font-medium">수도면적비율:</span> {formatRatio(calc.ratios.waterAreaContribution)}</div>
                    <div><span className="font-medium">전기비율:</span> {formatRatio(calc.ratios.electricityIndividual)}</div>
                    <div><span className="font-medium">수도비율:</span> {formatRatio(calc.ratios.waterIndividual)}</div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 요약 정보 */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">총 전기 요금:</span>
            <div className="font-medium">
              {formatCurrency(
                calculations.reduce((sum, calc) => sum + calc.electricity.totalFee, 0)
              )}
            </div>
          </div>
          <div>
            <span className="text-gray-500">총 수도 요금:</span>
            <div className="font-medium">
              {formatCurrency(
                calculations.reduce((sum, calc) => sum + calc.water.totalFee, 0)
              )}
            </div>
          </div>
          <div>
            <span className="text-gray-500">총 기타 요금:</span>
            <div className="font-medium">
              {formatCurrency(
                calculations.reduce((sum, calc) => 
                  sum + calc.electricity.fund + calc.electricity.offsetFee, 0
                )
              )}
            </div>
          </div>
          <div>
            <span className="text-gray-500">총 요금:</span>
            <div className="font-bold text-lg">
              {formatCurrency(
                calculations.reduce((sum, calc) => 
                  sum + calc.electricity.totalFee + calc.water.totalFee + 
                  calc.electricity.fund + calc.electricity.offsetFee, 0
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomBillingTable;
